import { useQuery } from '@tanstack/react-query';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

import { useAuth } from './AuthProvider';

export type Me = components['schemas']['Me'];
export type Entitlements = components['schemas']['Entitlements'];
export type EnarmItem = components['schemas']['EnarmCatalogItem'];
export type Certificates = components['schemas']['Certificates'];

export function useMe() {
  const { status } = useAuth();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => unwrap<Me>(api.GET('/api/v1/me')),
    enabled: status === 'authenticated',
    staleTime: 5 * 60_000,
  });
}

/** Permisos, membresías y límites: la UI nunca los calcula (plan §1). */
export function useEntitlements() {
  const { status } = useAuth();
  return useQuery({
    queryKey: ['entitlements'],
    queryFn: () => unwrap<Entitlements>(api.GET('/api/v1/me/entitlements')),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });
}

/** Cartas y constancias; `eligible` (solo quien compró un curso) lo decide la API. */
export function useConstancias() {
  const { status } = useAuth();
  return useQuery({
    queryKey: ['constancias'],
    queryFn: () => unwrap<Certificates>(api.GET('/api/v1/certificates')),
    enabled: status === 'authenticated',
    staleTime: 5 * 60_000,
  });
}

export function useEnarmCatalog() {
  return useQuery({
    queryKey: ['catalogs', 'enarm'],
    queryFn: () => unwrap<EnarmItem[]>(api.GET('/api/v1/catalogs/enarm')),
    staleTime: 60 * 60_000,
  });
}

export function useCan(permission: string | undefined): boolean | undefined {
  const { data } = useEntitlements();
  if (!permission) return true;
  if (!data) return undefined;
  return data.permissions.includes(permission);
}
